import { RegisterOptions, Validate } from 'react-hook-form'
import { TFunction } from 'next-i18next'
import { DateOfBirthFieldSchema } from '@afterpaytouch/form'
import { AllCountryCode, hasUnitOrStreetNumber, InputAddressData } from '@afterpaytouch/core'
import { isValidEmail } from '../../utils/validation'
import { getStateFullFromFormData } from './helper'
import { isStateEligibleForPayByInstallment } from '../../utils/profile-validation'
import { validAddressOption } from '../ShippingAddressForm/validations'
import { MAX_AGE_RESTRICTION, MINIMUM_AGE_RESTRICTION } from './constants'
import { Country } from '@afterpay/types'
import { ConvergenceTFunction } from '../../model'

export const namePattern =
  (t: TFunction | ConvergenceTFunction) =>
  (name: string): RegisterOptions => {
    const validationRegex = /^$|^[a-zA-Z\u00C0-\u017F][a-zA-Z\u00C0-\u017F- ']*$/
    return {
      pattern: {
        value: validationRegex,
        message: t('signUp:error.invalidName'),
      },
    }
  }

const MINIMUM_LENGTH_VALIDATION: { [x: string]: number } = {
  NZ: 2,
}

export const validName = (name: string): boolean => {
  const INVALID_NAMES = ['registered', 'guest', 'user']
  const isValidName = !INVALID_NAMES.includes(name.toLowerCase())
  return isValidName
}

export const nameMinimumLengthCheck = (name: string, country: AllCountryCode): boolean => {
  const minimumLengthCheck = MINIMUM_LENGTH_VALIDATION[country ?? 'AU'] ?? 0
  const isValidLength = name.length >= minimumLengthCheck
  return isValidLength
}

export const nameValidation = (
  t: TFunction | ConvergenceTFunction
): {
  validate: {
    validUserName: (v: string) => boolean | string
  }
} => {
  return {
    validate: {
      validUserName: (v) => {
        const isValidName = validName(v)
        return isValidName || t('signUp:error.unacceptedName')
      },
    },
  }
}

export const nameValidationWithLength = ({
  name,
  country,
  t,
}): {
  validate: {
    validUserName: (v: string) => boolean | string
    validNameLength: (v: string, country: AllCountryCode) => boolean | string
  }
} => {
  return {
    validate: {
      validUserName: (v) => {
        const isValidName = validName(v)
        return isValidName || t('signUp:error.unacceptedName')
      },
      validNameLength: (v) => {
        const isValidLength = nameMinimumLengthCheck(v, country)
        return isValidLength || t('signUp:error.invalidNameLength', { context: name })
      },
    },
  }
}

export const emailValidation = (t: TFunction | ConvergenceTFunction) => (name: string) => {
  return {
    validate: {
      validEmail: (v) => {
        const isValidEmailInput = isValidEmail(v)
        return isValidEmailInput || t('signUp:error.email.invalidEmail')
      },
    },
  }
}

export const requiredValidation =
  (t: TFunction | ConvergenceTFunction) =>
  (name: string): RegisterOptions => ({
    required: t('signUp:error.required', { context: name }),
  })

export const getDOBValidations = (t: TFunction | ConvergenceTFunction, address?: InputAddressData, country?: Country): Omit<DateOfBirthFieldSchema, 'type'> => {
  const countryMinimumAgeRestriction = MINIMUM_AGE_RESTRICTION[country ?? ''] ?? MINIMUM_AGE_RESTRICTION.DEFAULT_COUNTRY
  const minAgeValue = countryMinimumAgeRestriction[address?.state ?? ''] ?? countryMinimumAgeRestriction.DEFAULT

  return {
    minAge: {
      value: minAgeValue,
      message: t('signUp:error.underAge', { minAge: minAgeValue }),
    },
    maxAge: {
      value: MAX_AGE_RESTRICTION,
      message: t('signUp:error.overAge', { maxAge: MAX_AGE_RESTRICTION }),
    },
  }
}

export const requiredAddress = ({ name, country, t }): Record<string, Validate<InputAddressData, any>> => {
  return {
    requiredAddressFields: (v, _) => {
      const hasRequiredAddressFields = validAddressOption(v, country)
      return hasRequiredAddressFields || t('signUp:error.blankAddress', { context: name })
    },
    requireUnitOrStreetNumber: (v, _) => {
      if (country === 'AU') {
        return hasUnitOrStreetNumber(v.address1) || t('signUp:error.unitOrStreetNumberError')
      }

      return true
    },
  }
}

export const validateState = (
  isStateEligibleForPayByInstallmentEnabled: boolean,
  t: TFunction | ConvergenceTFunction
): Record<string, Validate<InputAddressData, any>> => {
  return {
    validState: (v, _) => {
      const stateFull = getStateFullFromFormData({ countryCode: v.countryCode, state: v.state }, t('signUp:error.blacklistedStateDefaultState'))
      const hasNoPayByInstallmentError = isStateEligibleForPayByInstallmentEnabled
        ? isStateEligibleForPayByInstallment({ countryCode: v.countryCode, state: v.state })
        : true
      const blacklistedStateErrorMsg = t('signUp:error.blacklistedState', { stateFull })
      return hasNoPayByInstallmentError || blacklistedStateErrorMsg
    },
  }
}
